import React, { useEffect, useState } from 'react';
import { aopApi, agentLabel, formatWhen, prettyActionType, AGENT_LABELS } from '../aopApi';

const pillStyle = { width: 'auto', background: 'var(--pill)', border: 'none', color: 'var(--text-sec)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 500, padding: '5px 28px 5px 10px' };

export default function PendingActions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [filterAgent, setFilterAgent] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');

  const load = () => {
    setLoading(true);
    aopApi
      .pendingActions({ limit: 200 })
      .then((res) => {
        if (!res.success) throw new Error(res.error || 'Failed to load pending actions');
        setRows(res.actions || []);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const decide = async (id, decision) => {
    setBusyId(id);
    try {
      const res =
        decision === 'approved'
          ? await aopApi.approve(id)
          : decision === 'needs_changes'
            ? await aopApi.requestChanges(id)
            : await aopApi.reject(id);
      if (!res.success) throw new Error(res.error || 'Decision failed');
      setRows((prev) => prev.map((r) => (r._id === id ? res.action : r)));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const filtered = rows.filter((r) => {
    if (filterAgent !== 'all' && r.sourceAgent !== filterAgent) return false;
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (filterRisk !== 'all' && r.riskLevel !== filterRisk) return false;
    return true;
  });

  const count = (status) => rows.filter((r) => r.status === status).length;

  return (
    <div>
      <div className="aop-page-header">
        <div>
          <h1 className="aop-page-title">Pending Actions</h1>
          <p className="aop-page-subtitle">Review and approve or reject agent-requested write actions before execution</p>
        </div>
        <div className="aop-page-actions">
          <button className="aop-btn ghost" onClick={load}>Refresh</button>
        </div>
      </div>

      {/* Stats */}
      <div className="aop-stats-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
        {[
          { label: 'Pending Review', value: count('pending'), color: 'var(--orange)' },
          { label: 'Approved', value: count('approved'), color: 'var(--green)' },
          { label: 'Rejected', value: count('rejected'), color: 'var(--red)' },
          { label: 'Total Actions', value: rows.length, color: 'var(--blue)' },
        ].map((s) => (
          <div key={s.label} className="aop-stat-card">
            <div className="aop-stat-value" style={{ color: s.color }}>{s.value}</div>
            <div className="aop-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="aop-filter-bar">
        <select className="aop-pill" value={filterAgent} onChange={(e) => setFilterAgent(e.target.value)} style={pillStyle}>
          <option value="all">All Agents</option>
          {Object.entries(AGENT_LABELS).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
        <select className="aop-pill" value={filterRisk} onChange={(e) => setFilterRisk(e.target.value)} style={pillStyle}>
          <option value="all">All Risk Levels</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select className="aop-pill" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={pillStyle}>
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="needs_changes">Needs Changes</option>
          <option value="expired">Expired</option>
        </select>
        <div className="aop-filter-bar-spacer" />
        <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{filtered.length} actions</span>
      </div>

      {error && (
        <div style={{ background: 'rgba(255,98,93,0.1)', border: '1px solid rgba(255,98,93,0.25)', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--red)' }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div className="aop-table-wrap">
        <table className="aop-table">
          <thead>
            <tr>
              <th>Action</th>
              <th>Agent</th>
              <th>Target</th>
              <th>Risk</th>
              <th>Requested</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7}><div className="aop-empty">Loading…</div></td></tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="aop-empty">
                    <div className="aop-empty-icon">✓</div>
                    {rows.length === 0 ? 'No pending actions yet — run a workflow to generate some' : 'No actions match the current filters'}
                  </div>
                </td>
              </tr>
            ) : filtered.map((row) => (
              <tr key={row._id} style={{ cursor: 'default' }}>
                <td>
                  <div className="cell-primary">{prettyActionType(row.actionType)}</div>
                  <div className="cell-sub">{row.summary || row.workflowRunId || ''}</div>
                </td>
                <td><span className={`aop-agent-tag ${row.sourceAgent}`}>{agentLabel(row.sourceAgent)}</span></td>
                <td><span className="text-muted">{row.targetSystem}</span></td>
                <td><span className={`aop-badge ${row.riskLevel}`}>{String(row.riskLevel || '').toUpperCase()}</span></td>
                <td><span className="text-muted">{formatWhen(row.createdAt)}</span></td>
                <td>
                  <span className={`aop-badge ${row.status}`}>{row.status.charAt(0).toUpperCase() + row.status.slice(1)}</span>
                  {row.status === 'pending' && row.stage && (
                    <div className="cell-sub" style={{ marginTop: 2 }}>{row.stage === 'client' ? 'client review' : 'internal review'}</div>
                  )}
                </td>
                <td>
                  <div className="actions-cell">
                    {row.status === 'pending' ? (
                      <>
                        <button className="aop-btn approve sm" disabled={busyId === row._id} onClick={() => decide(row._id, 'approved')}>Approve</button>
                        <button className="aop-btn reject sm" disabled={busyId === row._id} onClick={() => decide(row._id, 'rejected')}>Reject</button>
                        <button className="aop-btn sm" disabled={busyId === row._id} onClick={() => decide(row._id, 'needs_changes')}>Changes</button>
                      </>
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                        {row.approver ? `by ${row.approver}` : '—'}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="aop-pagination">
          <span>Showing {filtered.length} of {rows.length} actions</span>
        </div>
      </div>
    </div>
  );
}
